// FastaRepository.ts

import { inject, injectable } from "inversify";
import { FastaFile, PagedFastaFileList } from "@domain/schemas/BI";
import axios from "axios";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { IFastaRepository } from "@domain/repositories";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { deleteProjectStorageFile } from "./ProjectStorageFileDeletion";

const unwrapPagedResponse = (candidate: unknown): { source: Record<string, unknown>; list: FastaFile[] } => {
  if (!candidate || typeof candidate !== "object") {
    return { source: {}, list: [] };
  }

  const asRecord = candidate as Record<string, unknown>;
  const directList = asRecord.list ?? asRecord.List;
  if (Array.isArray(directList)) {
    return { source: asRecord, list: directList as FastaFile[] };
  }

  const nestedCandidate = asRecord.value ?? asRecord.Value ?? asRecord.data ?? asRecord.Data;
  if (nestedCandidate && typeof nestedCandidate === "object") {
    const nestedRecord = nestedCandidate as Record<string, unknown>;
    const nestedList = nestedRecord.list ?? nestedRecord.List;
    if (Array.isArray(nestedList)) {
      return { source: nestedRecord, list: nestedList as FastaFile[] };
    }
  }

  return { source: asRecord, list: [] };
};

@injectable()
export class FastaRepository implements IFastaRepository {
  private static readonly listCache = new Map<string, PagedFastaFileList>();
  private static readonly inFlightListRequests = new Map<string, Promise<PagedFastaFileList>>();

  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getListAsync(projectId: string, sourcePath?: string | null, folder?: string | null): Promise<PagedFastaFileList> {
    const cacheKey = `${projectId}::${sourcePath ?? ""}::${folder ?? ""}`;
    const cached = FastaRepository.listCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const inFlightRequest = FastaRepository.inFlightListRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.fastaListFiles}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const request = (async () => {
      try {
        const response = await this._requestHandlerService.handleRequestAsync<PagedFastaFileList>(() =>
          axios.get<PagedFastaFileList>(url, { params: { sourcePath: sourcePath || undefined, folder: folder || undefined } }),
        );

        const { source, list } = unwrapPagedResponse(response);
        const normalizedList = list.map((item) => {
          const record = item as unknown as {
            fileName?: string;
            FileName?: string;
            location?: string;
            Location?: string;
            createdBy?: string;
            CreatedBy?: string;
            size?: number;
            Size?: number;
            sourcePath?: string | null;
            SourcePath?: string | null;
            [key: string]: unknown;
          };

          return {
            ...item,
            fileName: String(record.fileName ?? record.FileName ?? ""),
            location: String(record.location ?? record.Location ?? ""),
            createdBy: String(record.createdBy ?? record.CreatedBy ?? ""),
            size: Number(record.size ?? record.Size ?? 0),
            sourcePath: record.sourcePath ?? record.SourcePath ?? null,
          } as FastaFile;
        });

        const normalizedResponse = {
          ...source,
          list: normalizedList,
        } as PagedFastaFileList;

        FastaRepository.listCache.set(cacheKey, normalizedResponse);
        return normalizedResponse;
      } finally {
        FastaRepository.inFlightListRequests.delete(cacheKey);
      }
    })();

    FastaRepository.inFlightListRequests.set(cacheKey, request);
    return request;
  }

  public async uploadFileAsync(projectId: string, file: File, onProgress: (progress: number) => void, rootPath?: string, folder?: string): Promise<FastaFile> {
    const formData = new FormData();
    formData.append("file", file);

    let url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.fastaCreate}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const query = new URLSearchParams();
    if (rootPath) {
      query.set("rootPath", rootPath);
    }
    if (folder) {
      query.set("folder", folder);
    }
    const queryString = query.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const uploadedFile = await this._requestHandlerService.handleRequestAsync<FastaFile>(() =>
      axios.post<FastaFile>(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            return;
          }

          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        },
      }),
    );

    FastaRepository.listCache.clear();
    FastaRepository.inFlightListRequests.clear();
    return uploadedFile;
  }

  public async checkFileExistsAsync(projectId: string, fileName: string, rootPath?: string, folder?: string): Promise<number | null> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.fastaCheckFile}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const params: Record<string, string> = { fileName };
    if (rootPath) {
      params.rootPath = rootPath;
    }
    if (folder) {
      params.folder = folder;
    }
    return this._requestHandlerService.handleRequestAsync<number | null>(() => axios.get<number | null>(url, { params }));
  }

  public async deleteFileAsync(projectId: string, fileName: string, folder: string | undefined, rootPath?: string | null): Promise<boolean> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.fastaDelete}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const wasDeleted = await deleteProjectStorageFile(this._requestHandlerService, url, {
      fileName,
      folder,
      rootPath: rootPath ?? undefined,
    });

    if (wasDeleted) {
      FastaRepository.listCache.clear();
      FastaRepository.inFlightListRequests.clear();
    }

    return wasDeleted;
  }
}
